import React, { useCallback, useMemo, useState } from 'react';
import { useConnections } from '@integration-app/react';
import { ProviderSelect } from 'src/pages/Automations/components/ui/ProviderSelect';
import { Button } from 'src/components/Button';
import { Input } from 'src/components/Input';
import { Label } from 'src/components/Label';
import { PromptTextArea } from 'src/components/PromptTextArea';
import { Agent, PostCallFinalizedEmail } from 'src/interfaces/agent.interface';
import { extractVariables } from 'src/utils/extractVariables';
import { POST_CALL_VARIABLES } from 'src/constants';
import { useIntegrationAppProviders } from 'src/hooks/useIntegrationAppProviders';
import { ConditionsList } from './conditions/ConditionsList';
import { UiCondition } from './conditions/types';
import {
  groupFilterToUiConditions,
  uiConditionsToGroupFilter,
} from './conditions/mappers';

type PostCallEmailSectionProps = {
  agent: Agent;
  setAgent: (value: React.SetStateAction<Agent>) => void;
  loading?: boolean;
};

const ensurePostCallEmail = (
  a: Agent,
  providerId?: string,
): PostCallFinalizedEmail => ({
  provider_config: {
    destination_id:
      providerId ??
      (a.post_call_email?.provider_config as { destination_id?: string })
        ?.destination_id ??
      '',
  },
  from_email: a.post_call_email?.from_email ?? '',
  to_email: a.post_call_email?.to_email ?? '',
  subject: a.post_call_email?.subject ?? '',
  body: a.post_call_email?.body ?? '',
});

export const PostCallEmailSection: React.FC<PostCallEmailSectionProps> = ({
  agent,
  setAgent,
  loading = false,
}) => {
  const { connections } = useConnections();
  const { emailProviders } = useIntegrationAppProviders();

  const [setupVisible, setSetupVisible] = useState<boolean>(
    !!agent.post_call_email,
  );

  const providerId =
    ((agent.post_call_email?.provider_config as { destination_id?: string })
      ?.destination_id ??
      '') ||
    '';

  const isDisabled = loading || (!agent.user_id && !!agent.id);

  const isProviderAuthed = useMemo(
    () =>
      !!connections.find(
        (c) => c.integration?.key === providerId && !c.disconnected,
      ),
    [connections, providerId],
  );

  const providersByValue = useMemo(
    () => Object.fromEntries(emailProviders.map((p) => [p.value, p.label])),
    [emailProviders],
  );

  const selectedProviderOption = useMemo(
    () => ({
      value: providerId,
      label: providersByValue[providerId] ?? 'Select a provider',
    }),
    [providerId, providersByValue],
  );

  const updatePostCallEmail = useCallback(
    (patch: Partial<PostCallFinalizedEmail>) =>
      setAgent((prev) => {
        const base = ensurePostCallEmail(
          prev,
          patch.provider_config?.destination_id,
        );
        return {
          ...prev,
          post_call_email: {
            ...base,
            ...patch,
            provider_config: {
              ...base.provider_config,
              ...patch.provider_config,
            },
          },
        };
      }),
    [setAgent],
  );

  const handleProviderChange = useCallback(
    (providerOption: { value: string; label?: string }) => {
      updatePostCallEmail({
        provider_config: { destination_id: providerOption.value },
      });
      if (!setupVisible) setSetupVisible(true);
    },
    [updatePostCallEmail, setupVisible],
  );

  const handleRemoveSetup = useCallback(() => {
    setSetupVisible(false);
    setAgent((prev) => ({ ...prev, post_call_email: null }));
  }, [setAgent]);

  // Conditions UI state (owned locally after initial mount)
  const [emailConditions, setEmailConditions] = useState<UiCondition[]>(() =>
    groupFilterToUiConditions(agent.post_call_email?.conditions),
  );

  const handleConditionsChange = useCallback(
    (next: UiCondition[]) => {
      setEmailConditions(next);
      const filter = uiConditionsToGroupFilter(next);
      updatePostCallEmail({ conditions: filter });
    },
    [updatePostCallEmail],
  );

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex justify-between items-start gap-4">
        <h4>Send Email</h4>
        {setupVisible && (
          <Button
            variant="outlined"
            color="error"
            data-testid="button-dont-send-email"
            onClick={handleRemoveSetup}
            className="whitespace-nowrap"
            size="small"
          >
            {"Don't Send an Email"}
          </Button>
        )}
      </div>

      {!setupVisible ? (
        <div className="bg-neutral-50 rounded-lg border border-neutral-200 p-8 text-center space-y-4">
          <Button
            variant="outlined"
            color="primary"
            data-testid="button-configure-email"
            onClick={() => {
              // Initialize post_call_email immediately so save validation runs
              updatePostCallEmail({});
              setSetupVisible(true);
            }}
            className="whitespace-nowrap"
            size="small"
          >
            Configure Email
          </Button>
        </div>
      ) : (
        <>
          <div className="space-y-6">
            <ProviderSelect
              providers={emailProviders}
              selectedProvider={selectedProviderOption}
              onChange={handleProviderChange}
              isAuthenticated={isProviderAuthed}
              label="Email Provider"
              htmlFor="email-provider"
              className="w-full bg-neutral-0"
              compact
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="From Email"
                id="email-from"
                placeholder="from@your.company.com"
                type="email"
                value={agent.post_call_email?.from_email || ''}
                onChange={(e) =>
                  updatePostCallEmail({ from_email: e.target.value })
                }
                disabled={isDisabled}
              />
              <Input
                label="To Email"
                type="email"
                id="emailTo"
                placeholder="to@your.company.com"
                value={agent.post_call_email?.to_email || ''}
                onChange={(e) =>
                  updatePostCallEmail({ to_email: e.target.value })
                }
                disabled={isDisabled}
              />
            </div>
            <Input
              label="Subject Line"
              type="text"
              id="emailSubject"
              placeholder="Inbound call summary"
              value={agent.post_call_email?.subject || ''}
              onChange={(e) => updatePostCallEmail({ subject: e.target.value })}
              disabled={isDisabled}
            />
            <div className="pb-6">
              <Label>Email Body</Label>
              <PromptTextArea
                value={agent.post_call_email?.body || ''}
                onChange={(e) => updatePostCallEmail({ body: e.target.value })}
                variables={Array.from(
                  new Set([
                    ...POST_CALL_VARIABLES,
                    ...extractVariables(agent.post_call_email?.body || ''),
                  ]),
                )}
                placeholder={`From: {from_number}\nTo: {to_number}\n\nSummary:\n{post_call_results}\n\nTranscript:\n{transcript}\n\n`}
                rows={8}
                loading={loading}
              />
            </div>
          </div>

          <div className="mt-2">
            <ConditionsList
              conditions={emailConditions}
              onChange={handleConditionsChange}
              disabled={isDisabled}
              labels={{
                withConditionsDescription:
                  'All conditions must be satisfied for an email to be sent',
                noConditionsDescription:
                  'Add conditions to control when an email is sent',
                noConditionsStateText:
                  'No conditions set. Emails will be sent after every call.',
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};
